import axios from "axios";
import endpoints from "@/endpoints";
import axiosApi from "@/axios";
import store from "@/store";

const { VUE_APP_PREPR_API } = process.env;

// One person's kits share a rootProfileId (the same grouping as the profile switcher).
const personOf = (kit) => kit.rootProfileId ?? kit.barcode;

/**
 * The kits endpoint lists every epigenetics kit on the account, which can be several
 * people's (yours and a parent's, say). Keep the kits of the person `profile` belongs to.
 * When `profile` isn't an epigenetics one, the kits are kept only if they're all one
 * person's: otherwise there's no telling whose results to show.
 */
export function kitsForProfile(kits, profile) {
    const own = kits.find(kit =>
        (profile?.id != null && kit.profileId === profile.id) || kit.barcode === profile?.barcode
    );
    if (own) return kits.filter(kit => personOf(kit) === personOf(own));
    return new Set(kits.map(personOf)).size <= 1 ? kits : [];
}

// Keep the trend points from `kits`. The API narrows the trend to one person when asked,
// but before that change deployed it covered the whole account.
export function trendForKits(trend, kits) {
    if (!trend) return trend;
    const barcodes = new Set(kits.map(kit => kit.barcode));
    return { ...trend, dataPoints: (trend.dataPoints ?? []).filter(point => barcodes.has(point.barcode)) };
}

const EpigeneticsService = {
    async getKits() {
        const { data } = await axiosApi.get(endpoints.epigeneticsKits);
        return data;
    },

    async getKitSummary(barcode) {
        const { data } = await axiosApi.get(endpoints.epigeneticsKitSummary(barcode));
        return data;
    },

    async getModuleDetail(barcode, moduleName) {
        const { data } = await axiosApi.get(endpoints.epigeneticsModuleDetail(barcode, moduleName));
        return data;
    },

    // Every person's points on the account, or one person's when given their rootProfileId.
    async getTrend(moduleName, rootProfileId = null) {
        const params = rootProfileId != null ? { rootProfileId } : {};
        const { data } = await axiosApi.get(endpoints.epigeneticsTrend(moduleName), { params });
        return data;
    },

    // The selected profile's kits, in the order the API lists them (newest first).
    async getProfileKits() {
        return (await EpigeneticsService.getProfileKitsInfo()).kits;
    },

    // As getProfileKits, plus whether the account has kits but none of them are this profile's.
    async getProfileKitsInfo() {
        const data = await EpigeneticsService.getKits();
        const all = data.kits ?? data;
        const kits = kitsForProfile(all, store.getters.profile?.profile);
        return { kits, onlyOthersHaveKits: kits.length === 0 && all.length > 0 };
    },

    // The selected profile's trend; pass `kits` when the caller already has them.
    async getProfileTrend(moduleName, kits = null) {
        const personKits = kits ?? await EpigeneticsService.getProfileKits();
        // One person's kits share a rootProfileId.
        const trend = await EpigeneticsService.getTrend(moduleName, personKits[0]?.rootProfileId);
        return trendForKits(trend, personKits);
    },

    // Prepr copy for the GrimAge systems components (CRP, PAI1, ...), one entry per component.
    async getSystemsComponents() {
        const { data } = await axios.post(VUE_APP_PREPR_API, {
            query: `{
                GrimAgeSystemsComponentss(limit: 50) {
                    items { component component_description }
                }
            }`,
        });
        return data?.data?.GrimAgeSystemsComponentss?.items ?? [];
    },
};

export default EpigeneticsService;
